"use client";

import { useEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { getAccountSessionSnapshot, subscribeAccountSession } from "@/lib/account-session";
import { resolveCustomerJourneyIdentity } from "@/lib/customer-journey-identity";
import { getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";
import {
  isHindiMobileJourney,
  mobileNavKeyForPath,
  type MobileNavKey,
} from "./mobile-bottom-bar-path";

const NAV_ID = "mobile-bottom-navigation";

function syncNavigation(
  pathname: string,
  journeyHref: string,
  journeyLabel: "My Jyotishi" | "My Reading"
): void {
  const navigation = document.getElementById(NAV_ID);
  if (!(navigation instanceof HTMLElement)) return;

  const language = isHindiMobileJourney(pathname) ? "hi" : "en";
  const activeKey = mobileNavKeyForPath(pathname);
  navigation.lang = language;
  navigation.setAttribute(
    "aria-label",
    language === "hi" ? "Mukhya mobile navigation" : "Primary mobile navigation"
  );

  navigation.querySelectorAll<HTMLAnchorElement>("a[data-mobile-nav-key]").forEach((link) => {
    const key = link.dataset.mobileNavKey as MobileNavKey | undefined;
    const localizedHref = language === "hi" ? link.dataset.hiHref : link.dataset.enHref;
    link.setAttribute("href", key === "journey" ? journeyHref : localizedHref || "/");
    const active = key === activeKey;
    link.toggleAttribute("aria-current", active);
    if (active) link.setAttribute("aria-current", "page");
    const label = link.querySelector("[data-mobile-nav-label]");
    if (label) {
      if (key === "journey" && journeyLabel === "My Reading") {
        label.textContent = language === "hi" ? "Meri Reading" : journeyLabel;
      } else {
        label.textContent =
          language === "hi" ? link.dataset.hiLabel || "" : link.dataset.enLabel || "";
      }
    }
  });
}

export default function MobileBottomBarState() {
  const pathname = usePathname() || "/";
  const accountSession = useSyncExternalStore(
    subscribeAccountSession,
    getAccountSessionSnapshot,
    () => null
  );
  const rememberedReading = useSyncExternalStore(
    subscribeLastReading,
    getLastReadingSnapshot,
    () => null
  );
  const journey = resolveCustomerJourneyIdentity({
    accountSession,
    authSubject: null,
    rememberedReading,
  });
  useEffect(
    () => syncNavigation(pathname, journey.primaryHref, journey.primaryLabel),
    [journey.primaryHref, journey.primaryLabel, pathname]
  );
  return null;
}
